// Derives the "Recommended Next Steps" list shown in the workflow rail.
//
// Deliberately a pure function over a snapshot of real account state: the
// Convex/demo plumbing lives in hooks/useWorkflowSnapshot.ts, so every rule
// below can be unit-tested without a provider tree. Nothing here is a static
// suggestion list. If a step appears, some real row (or the absence of one)
// put it there, and `why` says which.

import { ROUTE_META, CASE_AWARE_PATHS, resolveRouteKey } from '@/content/workflowMap';

export type StepTone = 'default' | 'progress' | 'urgent';

export interface RecommendedStep {
  /** Stable identity, used as the React key and in analytics. */
  key: string;
  label: string;
  /** Fully-resolved in-app href, case context already applied. */
  to: string;
  /** The evidence for this recommendation, in one sentence. */
  why: string;
  tone: StepTone;
}

export interface SnapshotCase {
  id: string;
  name: string;
  status: string;
  hasTheory: boolean;
}

export interface SnapshotSimulation {
  id: string;
  status: 'pending' | 'running' | 'complete' | 'failed';
  createdAt: number;
}

export interface SnapshotDeadline {
  title: string;
  date: string;
  status: 'upcoming' | 'urgent' | 'completed' | 'overdue';
}

export interface SnapshotTask {
  title: string;
  status: 'Not Started' | 'In Progress' | 'Completed';
  dueDate: string;
}

export interface WorkflowSnapshot {
  loading: boolean;
  caseCount: number;
  /** The case the rail is reasoning about (URL `?case=`, then storage, then newest). */
  activeCase?: SnapshotCase;
  /** Jurors on the active case. `undefined` means "not loaded", not "zero". */
  jurorCount?: number;
  /** Simulations for the active case, newest first is not assumed. */
  simulations: SnapshotSimulation[];
  deadlines: SnapshotDeadline[];
  tasks: SnapshotTask[];
}

export const EMPTY_SNAPSHOT: WorkflowSnapshot = {
  loading: true,
  caseCount: 0,
  simulations: [],
  deadlines: [],
  tasks: [],
};

const MAX_STEPS = 4;

/** Append `?case=<id>` to paths that actually read it. */
export function withCase(path: string, caseId?: string): string {
  if (!caseId || !CASE_AWARE_PATHS.has(path)) return path;
  return `${path}?case=${encodeURIComponent(caseId)}`;
}

function isPastDue(isoDate: string, now: number): boolean {
  // Dates are stored as plain YYYY-MM-DD strings. Compare against today's
  // local date string so a deadline is not "overdue" for the last few hours
  // of the day it is actually due.
  if (!isoDate) return false;
  const today = new Date(now);
  const pad = (n: number) => String(n).padStart(2, '0');
  const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  return isoDate.slice(0, 10) < todayStr;
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

/**
 * True when a step would just reload the page the user is already looking at.
 *
 * A query string earns an exception only if it does something: `?new=1` opens
 * the create-case dialog and `?tour=restart` replays onboarding, so those stay
 * useful on their own page. `?case=` only restates context, so a step carrying
 * nothing but that is dead weight.
 */
function targetsCurrentPage(to: string, routeKey: string | null): boolean {
  if (!routeKey) return false;
  const [path, queryString] = to.split('?');
  if (resolveRouteKey(path) !== routeKey) return false;
  const params = new URLSearchParams(queryString ?? '');
  for (const key of params.keys()) {
    if (key !== 'case') return false;
  }
  return true;
}

/**
 * @param routeKey  A key of ROUTE_META (from resolveRouteKey), or null.
 * @param snap      Current account state.
 * @param now       Injectable clock, for deterministic tests.
 */
export function getRecommendedSteps(
  routeKey: string | null,
  snap: WorkflowSnapshot,
  now: number = Date.now()
): RecommendedStep[] {
  if (snap.loading) return [];

  const caseId = snap.activeCase?.id;
  const caseName = snap.activeCase?.name;

  // ─── Cold start: nothing exists yet, so only one thing matters ─────────────
  if (snap.caseCount === 0) {
    return [
      {
        key: 'create-first-case',
        label: 'Create your first case',
        to: '/cases?new=1',
        why: 'Juries, arguments, and simulations all hang off a case.',
        tone: 'default',
      },
      {
        key: 'take-tour',
        label: 'Take the product tour',
        to: '/dashboard?tour=restart',
        why: 'Three minutes end to end, from case to verdict.',
        tone: 'default',
      },
      {
        key: 'see-example-report',
        label: 'See an example report',
        to: '/example-report',
        why: 'What a finished simulation actually gives you.',
        tone: 'default',
      },
    ];
  }

  const progress: RecommendedStep[] = [];
  const core: RecommendedStep[] = [];
  const urgent: RecommendedStep[] = [];

  const sims = [...snap.simulations].sort((a, b) => b.createdAt - a.createdAt);
  const inFlight = sims.find((s) => s.status === 'running' || s.status === 'pending');
  const latestComplete = sims.find((s) => s.status === 'complete');
  const completeCount = sims.filter((s) => s.status === 'complete').length;
  const latestFailed = sims[0]?.status === 'failed' ? sims[0] : undefined;
  const onCase = caseName ? ` on ${caseName}` : '';

  // ─── Something is happening right now ──────────────────────────────────────
  if (inFlight) {
    progress.push({
      key: `watch-sim-${inFlight.id}`,
      label: inFlight.status === 'running' ? 'Watch the running simulation' : 'Start the queued simulation',
      to: `/simulations/${inFlight.id}`,
      why:
        inFlight.status === 'running'
          ? `A simulation${onCase} is deliberating now.`
          : `A simulation${onCase} is queued and has not started.`,
      tone: 'progress',
    });
  } else if (latestFailed) {
    progress.push({
      key: `retry-sim-${latestFailed.id}`,
      label: 'Re-run the failed simulation',
      to: withCase('/simulations/new', caseId),
      why: `The last run${onCase} failed before returning a verdict.`,
      tone: 'urgent',
    });
  } else if (latestComplete && routeKey !== '/simulations/:id/results') {
    progress.push({
      key: `review-results-${latestComplete.id}`,
      label: 'Review the latest results',
      to: `/simulations/${latestComplete.id}/results`,
      why: `Your most recent verdict${onCase} is ready to read.`,
      tone: 'default',
    });
  }

  // ─── The core loop: case, jury, argument, simulate, compare ────────────────
  if (snap.activeCase) {
    if (snap.jurorCount === 0) {
      core.push({
        key: 'create-jury',
        label: 'Create a jury',
        to: withCase('/mock-panel', caseId),
        why: `${caseName} has no jurors yet, so there is nobody to argue to.`,
        tone: 'default',
      });
    } else if (snap.jurorCount !== undefined && sims.length === 0) {
      core.push({
        key: 'run-first-sim',
        label: 'Run the first simulation',
        to: withCase('/simulations/new', caseId),
        why: `${caseName} has ${snap.jurorCount} ${plural(snap.jurorCount, 'juror', 'jurors')} and no runs yet.`,
        tone: 'default',
      });
    } else if (completeCount === 1 && !inFlight) {
      core.push({
        key: 'run-variant',
        label: 'Test a second version',
        to: withCase('/simulations/new', caseId),
        why: `One run${onCase} is a data point. Two is a comparison.`,
        tone: 'default',
      });
    } else if (completeCount >= 2) {
      core.push({
        key: 'compare-sims',
        label: 'Compare your runs',
        to: withCase('/compare', caseId),
        why: `${completeCount} completed runs${onCase} are ready to put side by side.`,
        tone: 'default',
      });
    }

    if (!snap.activeCase.hasTheory) {
      core.push({
        key: 'add-case-theory',
        label: 'Add the case theory',
        to: `/cases/${snap.activeCase.id}`,
        why: `${caseName} has no case theory, and simulations read it for context.`,
        tone: 'default',
      });
    }
  }

  // ─── Time-sensitive admin ──────────────────────────────────────────────────
  const lateDeadlines = snap.deadlines.filter(
    (d) => d.status !== 'completed' && (d.status === 'overdue' || isPastDue(d.date, now))
  );
  const urgentDeadlines = snap.deadlines.filter((d) => d.status === 'urgent');
  if (lateDeadlines.length > 0) {
    urgent.push({
      key: 'overdue-deadlines',
      label: `Clear ${lateDeadlines.length} overdue ${plural(lateDeadlines.length, 'deadline', 'deadlines')}`,
      to: '/calendar',
      why:
        lateDeadlines.length === 1
          ? `"${lateDeadlines[0].title}" was due ${lateDeadlines[0].date}.`
          : `The earliest is "${lateDeadlines[0].title}", due ${lateDeadlines[0].date}.`,
      tone: 'urgent',
    });
  } else if (urgentDeadlines.length > 0) {
    urgent.push({
      key: 'urgent-deadlines',
      label: `${urgentDeadlines.length} ${plural(urgentDeadlines.length, 'deadline is', 'deadlines are')} marked urgent`,
      to: '/calendar',
      why: `Next up: "${urgentDeadlines[0].title}" on ${urgentDeadlines[0].date}.`,
      tone: 'urgent',
    });
  }

  const lateTasks = snap.tasks.filter((t) => t.status !== 'Completed' && isPastDue(t.dueDate, now));
  if (lateTasks.length > 0) {
    urgent.push({
      key: 'overdue-tasks',
      label: `${lateTasks.length} ${plural(lateTasks.length, 'task is', 'tasks are')} past due`,
      to: '/tasks',
      why: `Starting with "${lateTasks[0].title}".`,
      tone: 'urgent',
    });
  }

  // ─── Whatever this page naturally leads to ─────────────────────────────────
  const contextual: RecommendedStep[] = [];
  const meta = routeKey ? ROUTE_META[routeKey] : undefined;
  if (meta?.next) {
    contextual.push({
      key: `next-${routeKey}`,
      label: meta.next.label,
      to: withCase(meta.next.to, caseId),
      why: meta.next.why,
      tone: 'default',
    });
  }

  // Progress first (it is time-sensitive and already in motion), then the core
  // workflow gap, then admin, then the page-specific follow-on as filler.
  const ordered = [...progress, ...core, ...urgent, ...contextual];

  const seen = new Set<string>();
  const deduped: RecommendedStep[] = [];
  for (const step of ordered) {
    if (targetsCurrentPage(step.to, routeKey)) continue;
    const target = step.to.split('?')[0];
    if (seen.has(target)) continue;
    seen.add(target);
    deduped.push(step);
    if (deduped.length === MAX_STEPS) break;
  }
  return deduped;
}
